import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Star, Truck, Car, Zap, Sparkles, Bus, Mountain, Flame } from "lucide-react";

/**
 * Sticky chip-style category navigation that appears below the hero.
 *
 * Tapping a chip smooth-scrolls the page to the matching
 * `#category-{id}` section emitted by <CategoryCarousel>. The chip for
 * the section currently in view stays highlighted via an IntersectionObserver
 * — same pattern Airbnb uses on Explore.
 */
const ICONS = { Star, Truck, Car, Zap, Sparkles, Bus, Mountain, Flame };

export default function CategoryNav({ categories }) {
  const { i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const [activeId, setActiveId] = useState(null);
  const containerRef = useRef(null);

  // Observe each section so the chip auto-highlights as the user scrolls.
  useEffect(() => {
    if (!categories || categories.length === 0) return;
    const targets = categories
      .map((c) => document.getElementById(`category-${c.id}`))
      .filter(Boolean);
    if (targets.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.id.replace("category-", "");
          setActiveId(id);
        }
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.3, 0.6] },
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [categories]);

  // Auto-center the active chip in the strip so it never gets cut off.
  useEffect(() => {
    if (!activeId || !containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-chip="${activeId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeId]);

  const handleClick = (id) => {
    const target = document.getElementById(`category-${id}`);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  if (!categories || categories.length < 2) return null;

  return (
    <div
      className="sticky top-0 z-30 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 border-b border-neutral-100"
      data-testid="category-nav"
    >
      <div
        ref={containerRef}
        className="
          container-main
          flex gap-2 overflow-x-auto
          py-3
          [-ms-overflow-style:none] [scrollbar-width:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {categories.map((c) => {
          const Icon = ICONS[c.icon] || Car;
          const isActive = activeId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              data-chip={c.id}
              onClick={() => handleClick(c.id)}
              data-testid={`category-chip-${c.id}`}
              className={
                "shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors " +
                (isActive
                  ? "bg-[#111111] text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200")
              }
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium whitespace-nowrap">
                {isEn ? c.label_en || c.label_es : c.label_es}
              </span>
              <span
                className={
                  "text-xs " + (isActive ? "text-white/70" : "text-neutral-400")
                }
              >
                {c.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
